import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { AssistantMessageDTO, AssistantUndoableDTO } from '@contracts/assistant';
import type { Id } from '@contracts/common';
import { useAssistant } from './AssistantProvider';
import { useConversation, useOwnAppearance } from './api';
import { ConfirmCard } from './ConfirmCard';
import type { ExtEntry } from './useExtConfirm';
import { useBatchActions } from './ingest/api';
import { BatchCard, GoneBatch } from './ingest/BatchCard';
import { chipsFor } from './chips';
import { greetingOf } from './greeting';
import { Hana } from './Hana';
import { Markdown, endsWithChipRow, parseObjLinks, type ReplyChip } from './markdown';
import { ObjectChip, refKey, useResolved } from './ObjectChip';
import { usePageContext } from './page-context';
import { stepsOf, Trail, trailKey } from './Trail';
import { UndoCard } from './UndoCard';
import type { LiveTurn } from './useTurn';

/** The person has not scrolled up while within this many pixels of the bottom. */
const STICK_PX = 24;

const USER_BUBBLE = 'ml-auto max-w-[85%] whitespace-pre-wrap break-words rounded-[9px] px-2 py-[5px] '
  + 'bg-[hsl(var(--h-tone-primary-chip-bg))] text-[hsl(var(--h-card-foreground))]';
// The mockup's `.ai` (#f4f4f6): a neutral grey, not the primary-tinted muted surface.
export const AI_BUBBLE = 'mr-auto max-w-[92%] whitespace-pre-wrap break-words rounded-[9px] px-2 py-[5px] '
  + 'bg-[hsl(var(--h-secondary)/0.08)] text-[hsl(var(--h-card-foreground))]';
/** An assistant bubble holding Markdown blocks: they stack with a small gap. */
const AI_RICH = `${AI_BUBBLE} space-y-1.5`;
const ERROR_LINE = 'mr-auto max-w-[92%] rounded-[9px] border border-[hsl(var(--h-error)/0.45)] px-2 py-[5px] '
  + 'bg-[hsl(var(--h-error-soft))] text-[hsl(var(--h-error-fg))]';

/** The panel's suggestion chip (the starter chips and Hana's choice chips, D39): one family, one set of tokens. */
export const SUGGESTION_CHIP = 'min-h-11 rounded-full bg-[hsl(var(--h-tone-primary-chip-bg))] px-2.5 py-1 text-xs '
  + 'text-[hsl(var(--h-tone-primary-chip-fg))] transition-colors enabled:hover:bg-[hsl(var(--h-primary)/0.18)] lg:min-h-0';

/** What a message's choice chips do: send their text as the person's message, held while a turn runs. */
export type ReplyTo = { send: (text: string) => void; disabled: boolean };

/**
 * S5 D39: a choice chip Hana offered. A click sends its text the way the composer does, so it is the person's own
 * message (said, for the money rule); nothing sends it but a click. D44: what it sends is what it shows (the parser
 * keeps only a chip whose text is its label), so it needs no tooltip.
 */
function ReplyButton({ chip, to }: { chip: ReplyChip; to: ReplyTo }) {
  return (
    <button type="button" data-reply-chip="" disabled={to.disabled}
      onClick={() => to.send(chip.text)}
      className={`${SUGGESTION_CHIP} inline-flex items-center leading-snug disabled:cursor-default disabled:opacity-50`}>
      {chip.label}
    </button>
  );
}

/**
 * Hana's text: the safe Markdown subset, its object links as chips the server names (one resolve per message). Its
 * choice chips are buttons only when `replyTo` is given (the last message of the conversation); else plain text.
 */
export function Rich({ text, replyTo }: { text: string; replyTo?: ReplyTo | undefined }) {
  const refs = useMemo(() => parseObjLinks(text).map(({ type, id }) => ({ type, id })), [text]);
  const resolved = useResolved(refs);
  return (
    <Markdown text={text} renderObject={(o) => (
      <ObjectChip objectRef={{ type: o.type, id: o.id }} resolved={resolved.get(refKey(o))} />
    )} renderReply={replyTo === undefined ? undefined : (chip) => <ReplyButton chip={chip} to={replyTo} />} />
  );
}

/** D127: a message of a kind this bundle does not know comes from a newer server: ask for the newer app. */
function UnknownMessage() {
  return (
    <div data-unknown-message="" className="mr-auto flex max-w-[92%] flex-wrap items-center gap-2 rounded-[9px] border border-dashed border-[hsl(var(--h-border))] px-2 py-[5px] text-xs text-[hsl(var(--h-muted-foreground))]">
      <span className="min-w-0 flex-1">Tin nhắn này cần bản mới của ứng dụng. Tải lại trang để xem.</span>
      <button type="button" onClick={() => window.location.reload()}
        className={`${SUGGESTION_CHIP} inline-flex items-center font-medium`}>
        Tải lại
      </button>
    </div>
  );
}

/**
 * S8 D53 (mockup Màn 6): under the scope gate's refusal, a muted line saying the question never reached the main
 * model. Only the owner reads a conversation, so only the owner sees it.
 */
function ScopeNote() {
  // D163: lined up with the bubble, past the 26 px figure and the 8 px gap of its row.
  return <p data-scope-note="" className="mr-auto ml-[34px] px-2 text-[11px] text-[hsl(var(--h-muted-foreground))]">Không tính vào model chính</p>;
}

/**
 * Màn 6 (mockup `.aiw`, D163): an assistant bubble with the reader's own figure beside it, small and still, bottom
 * aligned. Decorative (the figure is aria-hidden); confused beside a refusal, else idle. Every own figure takes
 * `useOwnAppearance()` (D92, D93).
 */
function AiRow({ blocked = false, children }: { blocked?: boolean; children: ReactNode }) {
  const appearance = useOwnAppearance();
  return (
    <div data-ai-row="" className="flex min-w-0 items-end gap-2">
      <Hana size={26} still appearance={appearance} emotion={blocked ? 'confused' : 'idle'} className="shrink-0" />
      {children}
    </div>
  );
}

/** The trails the person opened, by `trailKey`: kept here, so the stored message keeps the live turn's open trail. */
type OpenTrails = { isOpen: (key: string | null) => boolean; toggle: (key: string | null) => void };

function useOpenTrails(): OpenTrails {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = useCallback((key: string | null) => {
    if (key === null) return;
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);
  return { isOpen: (key) => key !== null && open.has(key), toggle };
}

function Stored({ m, still, replyTo, trails }: {
  m: AssistantMessageDTO; still: boolean; replyTo?: ReplyTo | undefined; trails: OpenTrails;
}) {
  // S5 D53: a `batch` message is an ingest job's card; D127: a purged job's message keeps a line in the flow.
  const kind: string = m.kind;
  if (kind === 'batch') return m.jobId === null ? <GoneBatch /> : <BatchCard jobId={m.jobId} />;
  if (kind !== 'text') return <UnknownMessage />;
  if (m.role === 'user') return <p data-role="user" className={USER_BUBBLE}>{m.content}</p>;
  return (
    <div className="flex flex-col gap-1">
      {m.steps.length > 0 && (
        <Trail steps={stepsOf(m.steps)} folded still={still} expanded={trails.isOpen(trailKey(m.steps))}
          onToggle={() => trails.toggle(trailKey(m.steps))} />
      )}
      {/* A reply of steps only has no text: no empty bubble under its trail (P2 review F16). */}
      {m.content !== '' && (
        <AiRow blocked={m.blocked}>
          <div data-role="assistant" className={`${AI_RICH} min-w-0`}><Rich text={m.content} replyTo={replyTo} /></div>
        </AiRow>
      )}
      {m.blocked && <ScopeNote />}
    </div>
  );
}

/**
 * The undo cards to show: what the server lists as undoable, plus the ones undone here (the server drops those, and
 * the card then reads 'Đã hoàn tác'), each until its window ends; a timer at the soonest end drops it (D144).
 */
function useUndoCards(conversationId: Id | null, listed: readonly AssistantUndoableDTO[] | undefined,
  undone: ReadonlySet<Id>): AssistantUndoableDTO[] {
  const seen = useRef(new Map<Id, AssistantUndoableDTO>());
  const of = useRef(conversationId);
  if (of.current !== conversationId) { of.current = conversationId; seen.current = new Map(); }
  for (const u of listed ?? []) seen.current.set(u.groupId, u);   // D187: one card per command
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const now = Date.now();
  const listedIds = new Set((listed ?? []).map((u) => u.groupId));
  const cards = [...seen.current.values()].filter((u) => Date.parse(u.expiresAt) > now
    && (listedIds.has(u.groupId) || undone.has(u.groupId)));
  const next = cards.length === 0 ? null : Math.min(...cards.map((u) => Date.parse(u.expiresAt)));
  useEffect(() => {
    if (next === null) return undefined;
    const timer = window.setTimeout(rerender, Math.max(0, next - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [next]);
  return cards;
}

/**
 * The conversation (mockup `.me` / `.ai` bubbles, spec §8.1): the stored messages, then the live turn while it runs.
 * The live turn leaves only once the stored conversation holds a new assistant message and the typewriter has shown
 * the whole reply, so a typing animation is never cut by the refetch. It is the only part of the panel that scrolls.
 */
export function MessageList({ live, pollUntil, onSend, running, acting, onAct, ext, remember }: {
  live: LiveTurn | null; pollUntil: number | null; onSend: (text: string) => void;
  /** A turn is running: the cards wait. */
  running: boolean;
  /** Cards whose confirm or reject is on its way: hidden until the server says what became of them. */
  acting: ReadonlySet<Id>;
  onAct: (actionId: Id, kind: 'confirm' | 'reject') => void;
  /** S6 §7.3: external cards whose Xác nhận was pressed; a settled one stays after it left the pending list. */
  ext?: ReadonlyMap<Id, ExtEntry>;
  /** "Em đã nhớ" (D192, D215): the last thing in the log, after the undo and confirm cards, as the mockup draws it. */
  remember?: ReactNode;
}) {
  const { prefs, principal, conversationId, setConversationId, still, hanaUndo } = useAssistant();
  const { pathname } = useLocation();
  const page = usePageContext();
  const chips = chipsFor(page, pathname, prefs.personaName, new Date());
  const { data, error } = useConversation(conversationId, pollUntil);
  const trails = useOpenTrails();
  // P2 review F5: a remembered conversation the server no longer has (retention, deleted elsewhere) is forgotten, so
  // the panel greets afresh and the next message starts a new one.
  const gone = error?.status === 404 && error.code === 'NOT_FOUND';
  useEffect(() => { if (gone) setConversationId(null); }, [gone, setConversationId]);
  // §8.5: a batch is undone from its own card ("Hoàn tác"), never from a second undo card.
  const batchActions = useBatchActions((data?.messages ?? []).flatMap((m) => (m.kind === 'batch' && m.jobId !== null
    ? [m.jobId] : [])));
  const undoCards = useUndoCards(conversationId, data?.undoable, hanaUndo.undone)
    .filter((u) => !batchActions.has(u.actionId) && !batchActions.has(u.groupId));
  const messages = data?.messages ?? [];
  const pending = data?.pending ?? [];
  // An external card answered (or still out) after the server stopped listing it: kept with its outcome. A refused one
  // that is no longer pending (closed elsewhere) goes, as any card does.
  const settledExt = [...(ext?.values() ?? [])].filter((e) => e.outcome.state !== 'refused'
    && !pending.some((p) => p.actionId === e.card.actionId));
  const known = live === null ? null : new Set(live.known);
  // S5 D53: a batch card is no reply to the live turn, so only text messages count as the turn being caught up.
  const caughtUp = live !== null && known !== null && !live.typing
    && messages.some((m) => m.role === 'assistant' && m.kind === 'text' && !known.has(m.id))
    && (live.done || data?.running === false);
  const showLive = live !== null && !caughtUp;
  const shown = showLive && known !== null ? messages.filter((m) => known.has(m.id)) : messages;
  const empty = shown.length === 0 && !showLive;
  // Layer review F1: a settled external card sits right after the message it was confirmed under; only when that
  // message is not shown (out of the window, or none) does it fall to the bottom, with the cards.
  const shownIds = new Set<Id | null>(shown.map((m) => m.id));
  const settledCard = (e: ExtEntry) => (
    <ConfirmCard key={e.card.actionId} pending={e.card} outcome={e.outcome} disabled onAct={onAct} />
  );
  // Keeps the newest line in view while the person has not scrolled up.
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  /**
   * F8: a chip leaves the DOM once it is sent, which would drop the keyboard focus to <body>; it goes to the log
   * instead (never to the composer: that would raise a phone's keyboard).
   */
  const sendChip = (text: string) => {
    const el = box.current;
    if (el !== null && el.contains(document.activeElement)) el.focus({ preventScroll: true });
    onSend(text);
  };
  // D39: only the conversation's last message offers its choice chips; once anything follows, they are plain text.
  const replyTo: ReplyTo = { send: sendChip, disabled: running };
  const liveLast = showLive && (live.reply !== '' || live.typing || !live.done);
  const lastStored = showLive ? null : shown.at(-1) ?? null;

  useLayoutEffect(() => {
    const el = box.current;
    if (el !== null && stick.current) el.scrollTop = el.scrollHeight;
  });

  return (
    // D220: tabIndex -1 — the focus lands here when the last "Em đã nhớ" note leaves (never in the tab order).
    <div ref={box} role="log" aria-live="polite" tabIndex={-1}
      onScroll={(e) => {
        const el = e.currentTarget;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_PX;
      }}
      className="flex min-h-[min(10rem,calc(100%_-_1rem))] flex-1 flex-col gap-2 overflow-y-auto px-3 py-3 text-sm focus:outline-none">
      {data?.hasOlder === true && (
        <p className="text-center text-xs text-[hsl(var(--h-muted-foreground))]">Tin nhắn cũ hơn không hiển thị</p>
      )}
      {empty && (
        <div className="flex flex-col gap-2">
          <AiRow><p className={`${AI_BUBBLE} min-w-0`}>{greetingOf(prefs, principal)}</p></AiRow>
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <button key={chip} type="button" onClick={() => sendChip(chip)} className={SUGGESTION_CHIP}>
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}
      {shown.map((m) => (
        <Fragment key={m.id}>
          <Stored m={m} still={still} replyTo={m === lastStored ? replyTo : undefined} trails={trails} />
          {settledExt.filter((e) => e.after === m.id).map(settledCard)}
        </Fragment>
      ))}
      {showLive && (
        <>
          {live.userText !== '' && <p data-role="user" className={USER_BUBBLE}>{live.userText}</p>}
          <Trail steps={live.steps} folded={live.done} still={still} expanded={trails.isOpen(trailKey(live.steps))}
            onToggle={() => trails.toggle(trailKey(live.steps))} />
          {(live.reply !== '' || live.typing || !live.done) && (
            // The last paragraph runs inline, so the caret sits at the end of the text, not on a line of its own.
            <AiRow blocked={live.blocked}>
              <div data-live-reply="" aria-busy={live.typing} className={`${AI_RICH} min-w-0 [&>p:last-of-type]:inline`}>
                <Rich text={live.reply} replyTo={liveLast ? replyTo : undefined} />
                {(live.typing || (!live.done && live.error === null))
                  && !(liveLast && replyTo !== undefined && endsWithChipRow(live.reply)) && <span aria-hidden="true" className="hana-caret" />}
              </div>
            </AiRow>
          )}
          {/* Màn 6: the note sits under a finished refusal, never under one still typing. */}
          {live.blocked && !live.typing && <ScopeNote />}
        </>
      )}
      {live?.error != null && <p role="alert" className={ERROR_LINE}>{live.error.message}</p>}
      {/* The undo offers, then what waits on the person: the newest thing in the flow is the question. */}
      {undoCards.map((u) => (
        <UndoCard key={u.groupId} undoable={u} done={hanaUndo.undone.has(u.groupId)}
          gone={hanaUndo.gone.has(u.groupId)} onUndo={(g) => { void hanaUndo.undo(g); }}
          onCancelMission={hanaUndo.stale.has(u.groupId) ? () => { void hanaUndo.cancelMission(u.groupId); } : undefined} />
      ))}
      {settledExt.filter((e) => !shownIds.has(e.after)).map(settledCard)}
      {pending.filter((p) => !acting.has(p.actionId)).map((p) => (
        <ConfirmCard key={p.actionId} pending={p} disabled={running} onAct={onAct} outcome={ext?.get(p.actionId)?.outcome} />
      ))}
      {/* D215: "Em đã nhớ" closes the log, after the confirm card, as the mockup draws it. */}
      {remember}
    </div>
  );
}
